import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  useBggSearch,
  useCreateCustomGame,
  useGames,
  useGroupGames,
  useImportBggGame,
} from '../../api/hooks'
import type { BggSearchResponse, BggSearchResult } from '../../api/types'
import { GameThumb, ScorePad, ScorePadRow } from '../../primitives'
import { useRecordPlayDraft } from '../../stores/recordPlayDraft'

const bggMetaLine = (r: BggSearchResult): string => {
  const parts: string[] = []
  if (r.minPlayers && r.maxPlayers) {
    parts.push(
      r.minPlayers === r.maxPlayers ? `${r.minPlayers}p` : `${r.minPlayers}–${r.maxPlayers}p`,
    )
  }
  if (r.yearPublished) parts.push(String(r.yearPublished))
  if (r.rating !== null) parts.push(`${r.rating.toFixed(1)}★`)
  if (r.weight !== null) parts.push(`⚖${r.weight.toFixed(1)}`)
  return parts.length > 0 ? parts.join(' · ') : `bgg #${r.bggId}`
}

const useDebounced = (value: string, delayMs = 300): string => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

export const RecordGameStep = () => {
  const navigate = useNavigate()
  const groupId = useRecordPlayDraft((s) => s.groupId)
  const groupGames = useGroupGames(groupId ?? '')
  const allGames = useGames()
  const games = groupId ? groupGames : allGames
  const setGame = useRecordPlayDraft((s) => s.setGame)
  const currentGameId = useRecordPlayDraft((s) => s.gameId)
  const [filter, setFilter] = useState('')
  const debouncedQuery = useDebounced(filter, 350)
  const [showCustomForm, setShowCustomForm] = useState(false)

  const importBgg = useImportBggGame()
  const bgg = useBggSearch(debouncedQuery)

  const goPlayers = (gameId: string, defaultDurationMinutes?: number | null) => {
    setGame(gameId, defaultDurationMinutes)
    if (groupId) navigate({ to: '/group/$id/record/players', params: { id: groupId } })
    else navigate({ to: '/record/players' })
  }

  const filtered = (games.data ?? []).filter((g) =>
    g.name.toLowerCase().includes(filter.toLowerCase()),
  )
  const localBggIds = new Set(
    (games.data ?? []).map((g) => g.bggId).filter((id): id is number => id !== null),
  )
  const bggResults = (bgg.data?.results ?? []).filter((r) => !localBggIds.has(r.bggId))

  const onAddBgg = async (r: BggSearchResult) => {
    const game = await importBgg.mutateAsync(r.bggId)
    goPlayers(game.id, game.playingTime)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input
        type="search"
        value={filter}
        onChange={(e) => setFilter(e.currentTarget.value)}
        placeholder="Search games (BGG too)"
      />

      <div>
        <div className="kicker" style={{ padding: '0 4px 6px' }}>
          in your library
        </div>
        <ScorePad>
          {filtered.map((g) => (
            <ScorePadRow
              key={g.id}
              onClick={() => goPlayers(g.id, g.playingTime)}
              style={g.id === currentGameId ? { background: 'var(--terra-bg)' } : undefined}
            >
              <GameThumb label={g.name} imageUrl={g.imageUrl} size={44} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{g.name}</div>
                {g.minPlayers && g.maxPlayers && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {g.minPlayers === g.maxPlayers
                      ? `${g.minPlayers} players`
                      : `${g.minPlayers}–${g.maxPlayers} players`}
                  </div>
                )}
              </div>
            </ScorePadRow>
          ))}
          {filtered.length === 0 && (
            <ScorePadRow>
              <div style={{ color: 'var(--ink-3)' }}>
                {filter ? 'No matches in your library.' : 'No games yet.'}
              </div>
            </ScorePadRow>
          )}
        </ScorePad>
      </div>

      {debouncedQuery.trim().length >= 2 && (
        <BggSection
          query={debouncedQuery}
          loading={bgg.isLoading || bgg.isFetching}
          results={bggResults}
          available={bgg.data?.available ?? false}
          reason={bgg.data?.reason}
          onAdd={onAddBgg}
          adding={importBgg.isPending}
        />
      )}

      <CustomGameSection
        groupId={groupId ?? null}
        open={showCustomForm}
        onToggle={() => setShowCustomForm((s) => !s)}
        onCreated={(gameId) => {
          setShowCustomForm(false)
          goPlayers(gameId)
        }}
      />
    </div>
  )
}

const BggSection = ({
  query,
  loading,
  results,
  available,
  reason,
  onAdd,
  adding,
}: {
  query: string
  loading: boolean
  results: BggSearchResult[]
  available: boolean
  reason: BggSearchResponse['reason']
  onAdd: (r: BggSearchResult) => void
  adding: boolean
}) => {
  // Track which row's Add button is in flight so only that button shows the
  // "Adding…" label. The shared `adding` flag still disables every button to
  // prevent racing a second import while one is already navigating away.
  const [addingId, setAddingId] = useState<number | null>(null)
  const handleAdd = (r: BggSearchResult) => {
    setAddingId(r.bggId)
    onAdd(r)
  }
  return (
    <div>
      <div className="kicker" style={{ padding: '0 4px 6px' }}>
        from boardgamegeek
      </div>
      {loading && (
        <div className="felt-card" style={{ padding: 12, color: 'var(--ink-3)', fontSize: 12 }}>
          Searching…
        </div>
      )}
      {!loading && !available && (
        <div className="felt-card" style={{ padding: 12, color: 'var(--ink-2)', fontSize: 12 }}>
          {reason === 'no_token'
            ? 'BGG search not configured. Use a local game or create a custom one.'
            : reason === 'unauthorized'
              ? 'BGG token rejected. Check BGG_API_TOKEN.'
              : reason === 'rate_limited'
                ? 'BGG rate-limited. Try again in a minute.'
                : 'BGG unavailable.'}
        </div>
      )}
      {!loading && available && results.length === 0 && (
        <div className="felt-card" style={{ padding: 12, color: 'var(--ink-3)', fontSize: 12 }}>
          No BGG matches for "{query}".
        </div>
      )}
      {!loading && available && results.length > 0 && (
        <ScorePad>
          {results.slice(0, 8).map((r) => (
            <ScorePadRow key={r.bggId}>
              <GameThumb label={r.name} imageUrl={r.thumbnailUrl} size={36} palette="slate" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{bggMetaLine(r)}</div>
              </div>
              <button
                type="button"
                className="btn-ghost"
                style={{ padding: '4px 10px' }}
                onClick={() => handleAdd(r)}
                disabled={adding}
              >
                {addingId === r.bggId ? 'Adding…' : 'Add'}
              </button>
            </ScorePadRow>
          ))}
        </ScorePad>
      )}
    </div>
  )
}

const CustomGameSection = ({
  groupId,
  open,
  onToggle,
  onCreated,
}: {
  groupId: string | null
  open: boolean
  onToggle: () => void
  onCreated: (gameId: string) => void
}) => {
  const [name, setName] = useState('')
  const [minPlayers, setMinPlayers] = useState<number | null>(null)
  const [maxPlayers, setMaxPlayers] = useState<number | null>(null)
  const create = useCreateCustomGame()

  const onSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const game = await create.mutateAsync({
      name: trimmed,
      minPlayers,
      maxPlayers,
      groupId,
    })
    setName('')
    setMinPlayers(null)
    setMaxPlayers(null)
    onCreated(game.id)
  }

  if (!open) {
    return (
      <button type="button" className="btn-ghost" onClick={onToggle} style={{ width: '100%' }}>
        + Custom game
      </button>
    )
  }

  return (
    <div
      className="felt-card"
      style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div className="kicker">new custom game</div>
      <input
        type="text"
        placeholder="Name (e.g. Backyard Bowls)"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        autoFocus
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="kicker">min players</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={minPlayers ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value
              setMinPlayers(v === '' ? null : Number(v))
            }}
          />
        </label>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="kicker">max players</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={maxPlayers ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value
              setMaxPlayers(v === '' ? null : Number(v))
            }}
          />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn-ghost"
          onClick={onToggle}
          disabled={create.isPending}
          style={{ flex: 1 }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-stamp"
          onClick={onSubmit}
          disabled={create.isPending || !name.trim()}
          style={{ flex: 1 }}
        >
          {create.isPending ? 'Saving…' : 'Save game'}
        </button>
      </div>
      {create.isError && (
        <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>
          {(create.error as Error).message}
        </div>
      )}
    </div>
  )
}
